'use client';
import type { CSSProperties, ReactElement } from 'react';
import { motion } from 'framer-motion';
import { Ccontainer } from '../Ccontainer';
import type { CcontainerProps } from '../Ccontainer/Ccontainer.types';
import { pxToRem } from '@/theme/spacing';
import type { Temper, TextAlign } from '@/types/frontend-types';
import { CbullshitcodingLine } from './CbullshitcodingLine';
import { CbullshitcodingFlow } from './Cbullshitcoding.types';
import type { DisplayLine } from './Cbullshitcoding.typing';
interface CbullshitcodingViewportProps {
    completedLines: DisplayLine[];
    currentLine: DisplayLine | null;
    currentLineCursor: string;
    temper: Temper;
    textSize: number;
    align: TextAlign;
    isFadingOut: boolean;
    fadeDurationMs: number;
    style?: CSSProperties;
    fadeWrapperStyle?: CSSProperties;
    centerBlock: boolean;
    centeredBlockWidthCh: number | null;
    flow: CbullshitcodingFlow;
}
const COMPLETED_LINE_OPACITY = 0.6;
const CURRENT_LINE_OPACITY = 0.8;
const FADE_EASE = [0.4, 0, 0.2, 1] as const;
const EDGE_MASK_IMAGE = 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)';
const JUSTIFY_BY_FLOW: Record<CbullshitcodingFlow, NonNullable<CcontainerProps['justifyContent']>> = {
    [CbullshitcodingFlow.CenteredSoftEdges]: 'center',
    [CbullshitcodingFlow.TopUnmasked]: 'flex-start',
};
/** The mask is what softens a long log's first and last line; TopUnmasked drops it. */
const MASK_BY_FLOW: Record<CbullshitcodingFlow, string | undefined> = {
    [CbullshitcodingFlow.CenteredSoftEdges]: EDGE_MASK_IMAGE,
    [CbullshitcodingFlow.TopUnmasked]: undefined,
};
function getFadeWrapperWidth(input: {
    centerBlock: boolean;
    centeredBlockWidthCh: number | null;
}): string {
    return input.centerBlock && input.centeredBlockWidthCh != null
        ? `${input.centeredBlockWidthCh}ch`
        : '100%';
}
export function CbullshitcodingViewport({ completedLines, currentLine, currentLineCursor, temper, textSize, align, isFadingOut, fadeDurationMs, style, fadeWrapperStyle, centerBlock, centeredBlockWidthCh, flow, }: CbullshitcodingViewportProps): ReactElement {
    return <Ccontainer rootAttributes={{ "data-el": "Cbullshitcoding/Viewport/Root" }} width="100%" style={{
            overflow: 'visible',
            maskImage: MASK_BY_FLOW[flow],
            WebkitMaskImage: MASK_BY_FLOW[flow],
            ...style,
        }} flexDirection="column" justifyContent={JUSTIFY_BY_FLOW[flow]} padding={20} gap={4}>
            <motion.div data-el="Cbullshitcoding/Viewport/Track" animate={{ opacity: isFadingOut ? 0 : 1 }} transition={{ duration: fadeDurationMs / 1000, ease: FADE_EASE }} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: getFadeWrapperWidth({ centerBlock, centeredBlockWidthCh }),
            maxWidth: '100%',
            alignSelf: centerBlock ? 'center' : undefined,
            fontSize: centerBlock ? pxToRem(textSize) : undefined,
            ...fadeWrapperStyle,
        }}>
                {completedLines.map((line, index) => (<CbullshitcodingLine key={`line-${index}-${line.plain.substring(0, 5)}`} line={line} debugIdentity={line.plain} opacity={COMPLETED_LINE_OPACITY} temper={temper} textSize={textSize} align={align} cursor=""/>))}

                {currentLine ? (<CbullshitcodingLine line={currentLine} debugIdentity={currentLine.plain} opacity={CURRENT_LINE_OPACITY} temper={temper} textSize={textSize} align={align} cursor={currentLineCursor}/>) : null}
            </motion.div>
        </Ccontainer>
        ;
}
